import { type BigDecimalRangeDto, type Language, type MeasurementSystem, UNIT_SYMBOL_MAP } from '@ev-tech/search-modules-api';

import { type EnergyRange, type ListingDataTranslationKey, type ListingEnergyDescriptor, type ListingSummary } from './types';
import { formatNumber } from '../numberFormatters';

export const hasPositiveRangeValue = (range?: { min?: number; max?: number }): range is BigDecimalRangeDto =>
  (range?.min ?? 0) > 0 || (range?.max ?? 0) > 0;

export const hasEnergyRangeValue = (range?: EnergyRange): range is EnergyRange =>
  Boolean(
    range &&
    [range.min, range.max].some((value) => (typeof value === 'number' ? value > 0 : typeof value === 'string' ? value.length > 0 : false)),
  );

export const getEnergyRangeValue = (range: EnergyRange): number | string | undefined => {
  const values = [range.min, range.max];
  return values.find((value): value is number | string =>
    typeof value === 'number' ? value > 0 : typeof value === 'string' ? value.length > 0 : false,
  );
};

export const createPushEnergyRange =
  ({ descriptors, language }: { descriptors: ListingEnergyDescriptor[]; language: Language }) =>
  ({
    fieldName,
    labelKey,
    value,
    unitKey,
    numberFormatOptions,
  }: {
    fieldName: ListingEnergyDescriptor['fieldName'];
    labelKey: ListingDataTranslationKey;
    value: EnergyRange | undefined;
    unitKey?: ListingDataTranslationKey;
    numberFormatOptions?: Intl.NumberFormatOptions;
  }) => {
    if (!value || !hasEnergyRangeValue(value)) return;

    const energyValue = getEnergyRangeValue(value);
    if (energyValue === undefined) return;

    const formattedValue =
      typeof energyValue === 'number'
        ? formatNumber({ number: energyValue, language }, { maximumFractionDigits: 2, ...numberFormatOptions })
        : energyValue;

    descriptors.push(unitKey ? { fieldName, labelKey, value: formattedValue, unitKey } : { fieldName, labelKey, value: formattedValue });
  };

export const getListingUnit = (listing: ListingSummary, measurementSystem?: MeasurementSystem): string | undefined =>
  ('baseUnit' in listing ? listing.baseUnit : undefined) ?? (measurementSystem ? UNIT_SYMBOL_MAP[measurementSystem] : undefined);

export const formatListingAreaRange = ({
  value,
  language,
  unit,
  shouldShowRoundedValue,
  returnRangeValue,
}: {
  value: BigDecimalRangeDto;
  language: Language;
  unit?: string;
  shouldShowRoundedValue: boolean;
  returnRangeValue: boolean;
}): string => {
  const minimum = value.min && value.min > 0 ? value.min : undefined;
  const maximum = value.max && value.max > 0 ? value.max : undefined;
  const values = returnRangeValue && minimum && maximum && minimum !== maximum ? [minimum, maximum] : [minimum ?? maximum];
  const formattedValue = values
    .filter((areaValue): areaValue is number => areaValue !== undefined)
    .map((areaValue) =>
      formatNumber({ number: areaValue, language, shouldShowRoundedValue }, { maximumFractionDigits: shouldShowRoundedValue ? 0 : 1 }),
    )
    .join(' - ');

  return unit ? `${formattedValue} ${unit}` : formattedValue;
};
